import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Paper, Button, Alert, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { AdmissionCycle, AdmissionApplication, ApplicationStatus } from '../../../types/admissions';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useAuth } from '../../../context/AuthContext';
import { assessmentGaps, testOutcome, pendingOverride, eligibilityFailures, planFor } from '../../../utils/admissionAssessment';
import StatusChip, { ToneChip } from '../../common/StatusChip';
import TransitionDialog from '../dialogs/TransitionDialog';
import OfferAcceptanceDialog from '../dialogs/OfferAcceptanceDialog';
import OfferDeclineDialog from '../dialogs/OfferDeclineDialog';
import EnrollmentConfirmDialog from '../dialogs/EnrollmentConfirmDialog';
import ExtendOfferDialog from '../dialogs/ExtendOfferDialog';
import { grNumberOf } from '../../../utils/admissionRecords';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import { useUpcomingEmails } from '../../email/useUpcomingEmails';
import UpcomingEmailNotice from '../../email/UpcomingEmailNotice';
import { decisionEmailRequests } from '../decisionEmails';

/**
 * Decision tab — the next real step for this applicant, under this cycle's
 * settings. Every status move goes through TransitionDialog, so merit
 * order, the pass mark, the seat cap and fee steps are enforced (and
 * explained) the same way here as anywhere else.
 */

interface Props {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  onChanged: () => Promise<void> | void;
}

const idOf = (v: any): string => (v && typeof v === 'object' ? v._id : v) || '';
const num = { fontVariantNumeric: 'tabular-nums' } as const;

const CandidateDecisionTab: React.FC<Props> = ({ app, cycle, onChanged }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite, isAdmin } = useModulePermissions();
  const { decodedToken } = useAuth();
  const plan = useMemo(() => planFor(cycle, app), [cycle, app]);

  const [move, setMove] = useState<ApplicationStatus | null>(null);
  const [dialog, setDialog] = useState<'accept' | 'decline' | 'confirm' | 'extend' | null>(null);
  const [busy, setBusy] = useState(false);

  // Same split as the backend: pipeline steps need `admissions` write;
  // offer / waitlist / reject / not admitted / extend also need `admissions_decisions`.
  const canManage = canWrite('admissions');
  const canDecide = canManage && canWrite('admissions_decisions');
  const outcome = testOutcome(app, plan);
  const approval = pendingOverride(app);
  const mineRequest = approval && idOf(approval.request.proposedBy) === decodedToken?.userId;
  const canApprove = !!approval && canWrite('admissions_decisions_approve') && (!mineRequest || isAdmin);

  const done = async () => { setMove(null); setDialog(null); await onChanged(); };

  // Approving offers a seat outside the merit rules; declining closes the
  // request — both are confirmed first.
  const [confirmApproval, setConfirmApproval] = useState<'approved' | 'rejected' | null>(null);
  // Approving makes the offer, which may email the family.
  const approvalEmails = useMemo(
    () => (confirmApproval === 'approved' ? decisionEmailRequests([{ to: 'Offered', id: app._id }]) : []),
    [confirmApproval, app._id],
  );
  const upcoming = useUpcomingEmails(approvalEmails);

  const decideApproval = async (decision: 'approved' | 'rejected') => {
    if (!approval) return;
    setConfirmApproval(null);
    setBusy(true);
    try {
      await admissionApplicationService.decideDecisionOverride(app._id, approval.index, decision);
      enqueueSnackbar(decision === 'approved' ? 'Approved — seat offered' : 'Request declined', { variant: 'success' });
      await onChanged();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not record the decision', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const decisionButtons = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Button variant="contained" disableElevation color="success" disabled={!canDecide} onClick={() => setMove('Offered')}>Offer a seat</Button>
      <Button variant="outlined" disabled={!canDecide} onClick={() => setMove('Waitlisted')}>Waitlist</Button>
      <Button color="error" disabled={!canDecide} onClick={() => setMove('Rejected')}>Reject</Button>
    </Stack>
  );

  const content = (() => {
    if (approval) {
      const r = approval.request;
      return (
        <Step
          title="Waiting for approval"
          text={`Requested ${dateTimeUtils.formatDateForDisplay(r.proposedAt)} to offer a seat outside the merit rules.`}
          note={r.rationale}
        >
          {canApprove ? (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disableElevation color="success" disabled={busy} onClick={() => setConfirmApproval('approved')}>Approve and offer</Button>
              <Button color="inherit" disabled={busy} onClick={() => setConfirmApproval('rejected')} sx={{ color: 'text.secondary' }}>Decline</Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {mineRequest ? 'You made this request — another approver decides it.' : 'An approver will decide this request.'}
            </Typography>
          )}
        </Step>
      );
    }

    switch (app.status) {
      case 'Draft':
      case 'Submitted':
        return <Step title="Application being submitted" text="It moves to review once submission completes." />;
      case 'AppFeePending':
        return <Step title="Waiting for the application fee" text="Record the payment on the Fees tab — the application moves to review by itself." />;

      case 'UnderReview':
      case 'DocsRequired':
        return plan.hasAssessment ? (
          <Step title="Next: schedule the assessment" text="Check the documents, then schedule the entrance test or interview.">
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disableElevation disabled={!canManage} onClick={() => setMove('AssessmentScheduled')}>Schedule assessment</Button>
              <Button color="error" disabled={!canDecide} onClick={() => setMove('Rejected')}>Reject</Button>
            </Stack>
          </Step>
        ) : (
          <Step title="Make a decision" text="This cycle has no test or interview — decide from the review.">{decisionButtons}</Step>
        );

      case 'AssessmentScheduled': {
        const gaps = assessmentGaps(app, plan);
        return (
          <Step
            title="Assessment in progress"
            text={plan.admitCards && !app.admitCard?.rollNumber
              ? 'Issue the admit card, then enter the marks on the Assessment tab.'
              : 'Enter the marks on the Assessment tab — the application moves to Assessed once all are in.'}
            note={gaps.length ? `Still to mark: ${gaps.join(', ')}` : undefined}
          >
            <Button color="inherit" disabled={!canManage} onClick={() => setMove('Absent')} sx={{ color: 'text.secondary' }}>Mark absent</Button>
          </Step>
        );
      }
      case 'Absent':
        return (
          <Step title="Marked absent" text="Reschedule the assessment or close the application.">
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disableElevation disabled={!canManage} onClick={() => setMove('AssessmentScheduled')}>Reschedule</Button>
              <Button color="error" disabled={!canDecide} onClick={() => setMove('Rejected')}>Reject</Button>
            </Stack>
          </Step>
        );

      case 'Assessed':
        return <Step title="Make a decision" text="Offers follow the merit list; anything outside it goes to an approver.">{decisionButtons}</Step>;

      case 'Offered': {
        const offer = app.offer;
        const expired = !!offer && new Date(offer.expiresAt).getTime() < Date.now();
        return (
          <Step
            title={expired ? 'Offer deadline passed' : 'Seat offered'}
            text={!offer ? 'Waiting for the family to respond.'
              : expired
                ? `The family didn't accept by ${dateTimeUtils.formatDateForDisplay(offer.expiresAt)}, so the offer is lapsing and the seat will free up. Extend it if the family still wants the seat.`
                : `Offered ${dateTimeUtils.formatDateForDisplay(offer.issuedAt)}. If the family doesn't accept by ${dateTimeUtils.formatDateForDisplay(offer.expiresAt)}, the offer lapses on its own and the seat frees up.`}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" disableElevation color="success" disabled={expired || !canManage} onClick={() => setDialog('accept')}>Record acceptance</Button>
              <Button variant="outlined" disabled={!canManage} onClick={() => setDialog('decline')}>Record decline</Button>
              <Button color="inherit" disabled={!canDecide} onClick={() => setDialog('extend')} sx={{ color: expired ? 'primary.main' : 'text.secondary' }}>Extend offer</Button>
            </Stack>
          </Step>
        );
      }

      case 'Waitlisted':
        return (
          <Step
            title="On the waitlist"
            text={[
              app.meritRank != null && `Merit rank #${app.meritRank}`,
              app.waitlist?.enteredAt && `waitlisted ${dateTimeUtils.formatDateForDisplay(app.waitlist.enteredAt)}`,
            ].filter(Boolean).join(' · ') || 'Waiting for a seat to open.'}
          >
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disableElevation disabled={!canDecide} onClick={() => setMove('Offered')}>Offer a seat</Button>
              <Button color="inherit" disabled={!canDecide} onClick={() => setMove('NotAdmitted')} sx={{ color: 'text.secondary' }}>Close as not admitted</Button>
            </Stack>
          </Step>
        );

      case 'OfferAccepted':
      case 'ConfirmedConditional':
      case 'AdmFeePending':
        return <Step title="Waiting for the admission fee" text="Record the payment on the Fees tab — the seat is confirmed by itself once it's paid or waived." />;

      case 'Confirmed':
        return (
          <Step
            title="Seat confirmed"
            text={app.enrollmentConfirmation
              ? `Details confirmed ${dateTimeUtils.formatDateForDisplay(app.enrollmentConfirmation.confirmedAt)}. Enroll from Enrollment handover.`
              : 'Go through the final details with the family, then confirm them for enrollment.'}
          >
            <Stack direction="row" spacing={1}>
              {!app.enrollmentConfirmation && (
                <Button variant="contained" disableElevation color="success" disabled={!canManage} onClick={() => setDialog('confirm')}>Confirm for enrollment</Button>
              )}
              <Button variant={app.enrollmentConfirmation ? 'contained' : 'text'} disableElevation onClick={() => navigate('/app/admissions/handover')}>
                Open enrollment handover
              </Button>
            </Stack>
          </Step>
        );

      case 'Enrolled':
        return <Step title="Enrolled" text={`Enrolled as a student${grNumberOf(app) ? ` · GR ${grNumberOf(app)}` : ''}.`} />;

      default:
        return <Step title={CLOSED_COPY[app.status]?.title ?? 'Closed'} text={CLOSED_COPY[app.status]?.text ?? 'No further steps.'} />;
    }
  })();

  return (
    <>
      <Stack spacing={2}>
        {/* ── Standing ─────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 2 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
              <StatusChip status={{ kind: 'application', value: app.status }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Merit</Typography>
              {app.meritRank != null
                ? <Typography variant="body1" fontWeight={700} sx={num}>#{app.meritRank} <Typography component="span" variant="body2" color="text.secondary">· {app.computedMeritScore?.toFixed(2)}</Typography></Typography>
                : <Typography variant="body2" color="text.secondary">Not ranked</Typography>}
            </Box>
            {plan.test && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Test</Typography>
                {outcome
                  ? <ToneChip label={`${outcome.obtained}/${outcome.max} · ${outcome.passed ? 'passed' : 'below pass mark'}`} tone={outcome.passed ? 'success' : 'error'} />
                  : <Typography variant="body2" color="text.secondary">Not marked</Typography>}
              </Box>
            )}
          </Stack>
          {eligibilityFailures(app).length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
              {eligibilityFailures(app).join(' · ')}
            </Alert>
          )}
          {app.eligibilityWaiver && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
              Eligibility rules waived — {app.eligibilityWaiver.reason}
            </Alert>
          )}
        </Paper>

        {content}
      </Stack>

      {approval && (
        <ConfirmationDialog
          open={!!confirmApproval}
          onClose={() => setConfirmApproval(null)}
          onConfirm={() => confirmApproval && decideApproval(confirmApproval)}
          title={confirmApproval === 'approved' ? `Offer ${app.name} a seat?` : 'Decline this request?'}
          message={confirmApproval === 'approved'
            ? 'This offers a seat outside the merit rules and uses one of the class\'s seats. The offer\'s acceptance deadline starts now.'
            : 'The applicant stays where they are. Staff can send a new request later if needed.'}
          confirmText={confirmApproval === 'approved' ? (upcoming.sending ? 'Approve and send email' : 'Approve and offer') : 'Decline request'}
          confirmColor={confirmApproval === 'approved' ? 'success' : 'error'}
          confirmDisabled={upcoming.loading}
        >
          <ConfirmFacts items={[
            { label: 'Merit rank', value: app.meritRank != null ? `#${app.meritRank}` : 'Not ranked' },
            { label: 'Reason given', value: approval.request.rationale },
          ]} />
          {confirmApproval === 'approved' && <Box sx={{ mt: 1.5 }}><UpcomingEmailNotice upcoming={upcoming} /></Box>}
        </ConfirmationDialog>
      )}
      {move && (
        <TransitionDialog open application={app} cycle={cycle} targets={[move]} onClose={() => setMove(null)} onDone={done} />
      )}
      {dialog === 'accept' && (
        <OfferAcceptanceDialog open application={app} cycle={cycle} onCancel={() => setDialog(null)} onDone={done} />
      )}
      {dialog === 'decline' && (
        <OfferDeclineDialog open application={app} onCancel={() => setDialog(null)} onDone={done} />
      )}
      {dialog === 'extend' && (
        <ExtendOfferDialog open application={app} onCancel={() => setDialog(null)} onDone={done} />
      )}
      {dialog === 'confirm' && (
        <EnrollmentConfirmDialog open application={app} onCancel={() => setDialog(null)} onDone={done} />
      )}
    </>
  );
};

/** One step card: what's happening, then the actions for it. */
const Step: React.FC<{ title: string; text: string; note?: string; children?: React.ReactNode }> = ({ title, text, note, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 2 }}>
    <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{text}</Typography>
    {note && (
      <Box sx={{ mt: 1.25, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'action.hover' }}>
        <Typography variant="body2">{note}</Typography>
      </Box>
    )}
    {children && (
      <>
        <Divider sx={{ my: 1.75 }} />
        {children}
      </>
    )}
  </Paper>
);

const CLOSED_COPY: Partial<Record<ApplicationStatus, { title: string; text: string }>> = {
  OfferDeclined: { title: 'Offer declined', text: 'The family declined the seat.' },
  OfferLapsed:   { title: 'Offer lapsed',   text: 'The offer expired without a response.' },
  NotAdmitted:   { title: 'Not admitted',   text: 'Closed from the waitlist without a seat.' },
  Rejected:      { title: 'Rejected',       text: 'Not selected in this cycle.' },
  Ineligible:    { title: 'Ineligible',     text: 'Did not meet the eligibility rules.' },
  Withdrawn:     { title: 'Withdrawn',      text: 'The family withdrew the application.' },
};

export default CandidateDecisionTab;
